import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, BookOpen, Star, Trash2, Lightbulb, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { STANDARD_SEARCHES, useAiTemplates } from "../lib/aiPrompt";

const EXAMPLES = ["pr_ex_1", "pr_ex_2", "pr_ex_3", "pr_ex_4"];
const chip = "h-7 px-2.5 rounded-full text-xs border transition-colors";

function Library({ t, onPick, templates }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
                <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1.5"><BookOpen className="w-3.5 h-3.5" /> {t("pr_lib")}</div>
                <div className="space-y-1">
                    {STANDARD_SEARCHES.map((s) => (
                        <button key={s.id} data-testid={`pr-lib-${s.id}`} onClick={() => onPick({ title: t(s.name), prompt: t(s.name), fields: s.fields, full: s.full })}
                            className="w-full text-left px-2.5 py-1.5 rounded-md border border-[#1e293b] hover:bg-[#182232] hover:border-fuchsia-500/40 transition-colors">
                            <div className="text-sm font-semibold">{t(s.name)}</div>
                            <div className="text-[11px] text-slate-400 truncate">{s.full ? t("pr_lib_full_desc") : s.fields.map((f) => (f.key ? t(`ai_f_${f.key}`) : t(f.label))).join(", ")}</div>
                        </button>
                    ))}
                </div>
            </div>
            <div>
                <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-amber-300 mb-1.5"><Star className="w-3.5 h-3.5" /> {t("pr_mine")}</div>
                {!templates.list.length && <div data-testid="pr-mine-empty" className="text-xs text-slate-500 px-1">{t("pr_mine_empty")}</div>}
                <div className="space-y-1">
                    {templates.list.map((tp) => (
                        <div key={tp.id} className="flex items-center gap-1">
                            <button data-testid={`pr-mine-${tp.id}`} onClick={() => onPick({ title: tp.name, prompt: tp.prompt || tp.name, fields: tp.fields, saved: true })}
                                className="flex-1 min-w-0 text-left px-2.5 py-1.5 rounded-md border border-amber-500/30 hover:bg-amber-500/10 transition-colors">
                                <div className="text-sm font-semibold truncate">{tp.name}</div>
                                <div className="text-[11px] text-slate-400 truncate">{tp.prompt}</div>
                            </button>
                            <button data-testid={`pr-mine-del-${tp.id}`} onClick={() => templates.remove(tp.id)} className="p-1.5 text-slate-500 hover:text-rose-300"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Clarify({ t, clar, onDone, onBack }) {
    const [sel, setSel] = useState([]);
    const toggle = (i) => setSel((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));
    return (
        <div data-testid="pr-clarify">
            <div className="text-sm font-semibold mb-2">{clar.question || t("pr_clarify_q")}</div>
            <div className="space-y-1.5">
                {clar.options.map((o, i) => (
                    <label key={i} data-testid={`pr-clarify-opt-${i}`} className="flex items-center gap-2 px-3 py-2 rounded-md border border-[#1e293b] hover:bg-[#182232] cursor-pointer">
                        <input type="checkbox" checked={sel.includes(i)} onChange={() => toggle(i)} className="accent-fuchsia-500" />
                        <span className="text-sm">{o.label}</span>
                    </label>
                ))}
            </div>
            <div className="flex justify-end gap-2 mt-4">
                <button data-testid="pr-clarify-back" onClick={onBack} className="h-8 px-3 rounded text-xs border border-[#1e293b] text-slate-300 hover:bg-[#182232]">{t("pr_edit")}</button>
                <button data-testid="pr-clarify-next" disabled={!sel.length} onClick={() => onDone(sel.flatMap((i) => clar.options[i].fields))}
                    className="h-8 px-4 rounded text-xs font-semibold bg-fuchsia-600 hover:bg-fuchsia-500 text-white disabled:opacity-40">{t("pr_next")}</button>
            </div>
        </div>
    );
}

function Plan({ t, plan, setPlan, onRun, onEdit, templates }) {
    const [extra, setExtra] = useState("");
    const label = (f) => (f.key ? t(`ai_f_${f.key}`) : f.label.startsWith("pr_") ? t(f.label) : f.label);
    const toggle = (i) => setPlan((p) => ({ ...p, fields: p.fields.map((f, j) => (j === i ? { ...f, on: !f.on } : f)) }));
    const save = async () => {
        try {
            await templates.save(plan.title || plan.prompt, plan.prompt, plan.fields.filter((f) => f.on).map((f) => ({ key: f.key, label: label(f) })));
            setPlan((p) => ({ ...p, frequent: false, saved: true }));
            toast.success(t("pr_saved"));
        } catch { toast.error(t("pr_failed")); }
    };
    const add = () => { const l = extra.trim(); if (l) { setPlan((p) => ({ ...p, fields: [...p.fields, { key: null, label: l, on: true }] })); setExtra(""); } };
    return (
        <div data-testid="pr-plan">
            {plan.frequent && !plan.saved && (
                <div data-testid="pr-frequent" className="flex items-center gap-2 mb-3 rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs">
                    <Lightbulb className="w-4 h-4 text-amber-300 shrink-0" />
                    <span className="text-amber-100">{t("pr_frequent")}</span>
                    <button data-testid="pr-frequent-yes" onClick={save} className="ml-auto h-7 px-3 rounded bg-amber-500 text-black font-semibold hover:bg-amber-400">{t("mv_yes")}</button>
                </div>
            )}
            <div className="text-sm font-semibold mb-2">{t("pr_found_fields")}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {plan.fields.map((f, i) => (
                    <label key={`${f.key || f.label}-${i}`} data-testid={`pr-field-${f.key || `new-${i}`}`} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border cursor-pointer text-sm ${f.on ? "border-fuchsia-500/50 bg-fuchsia-500/10" : "border-[#1e293b] text-slate-400"}`}>
                        <input type="checkbox" checked={f.on} onChange={() => toggle(i)} className="accent-fuchsia-500" />
                        <span className="truncate" title={label(f)}>{label(f)}</span>
                        {!f.key && <span className="ml-auto text-[10px] text-fuchsia-300 shrink-0">{t("pr_new_col")}</span>}
                    </label>
                ))}
            </div>
            <div className="flex gap-1.5 mt-2">
                <input data-testid="pr-add-field" value={extra} onChange={(e) => setExtra(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder={t("pr_add_field")} maxLength={40}
                    className="flex-1 h-8 px-2 rounded bg-[#0b0f17] border border-[#1e293b] text-xs focus:outline-none focus:ring-1 focus:ring-fuchsia-500" />
                <button data-testid="pr-add-field-btn" onClick={add} disabled={!extra.trim()} className="h-8 w-8 rounded border border-[#1e293b] flex items-center justify-center hover:bg-[#182232] disabled:opacity-40"><Plus className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-2 mt-4">
                {!plan.saved && !plan.frequent && <button data-testid="pr-save-template" onClick={save} className="h-8 px-3 rounded text-xs border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 flex items-center gap-1"><Star className="w-3.5 h-3.5" /> {t("pr_save_tpl")}</button>}
                <span className="ml-auto text-sm text-slate-300">{t("pr_analyze_q")}</span>
                <button data-testid="pr-edit" onClick={onEdit} className="h-8 px-3 rounded text-xs border border-[#1e293b] text-slate-300 hover:bg-[#182232]">{t("pr_edit")}</button>
                <button data-testid="pr-run" autoFocus disabled={!plan.fields.some((f) => f.on)} onClick={() => onRun({ fields: plan.fields.filter((f) => f.on).map((f) => ({ key: f.key, label: label(f) })), task: plan.prompt, full: plan.full && plan.fields.every((f) => f.on) })}
                    className="h-8 px-4 rounded text-xs font-semibold bg-fuchsia-600 hover:bg-fuchsia-500 text-white disabled:opacity-40">{t("mv_yes")}</button>
            </div>
        </div>
    );
}

// Natural-language AI task ("Find all blind dimensions") -> AI proposes fields -> user confirms -> analysis.
export function AiPromptDialog({ open, onOpenChange, onRun }) {
    const { t } = useI18n();
    const templates = useAiTemplates();
    const [prompt, setPrompt] = useState("");
    const [busy, setBusy] = useState(false);
    const [step, setStep] = useState("input");
    const [clar, setClar] = useState(null);
    const [plan, setPlan] = useState(null);
    const toPlan = (p) => { setPlan({ ...p, fields: p.fields.map((f) => ({ ...f, on: true })) }); setStep("plan"); };
    const ask = async (clarified = false) => {
        if (prompt.trim().length < 2) return;
        setBusy(true);
        try {
            const { data } = await api.post("/ai/prompt-plan", { prompt: prompt.trim(), clarified });
            if (data.clarify) { setClar(data); setStep("clarify"); } else toPlan({ ...data, prompt: prompt.trim() });
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("pr_failed")); }
        finally { setBusy(false); }
    };
    const close = (o) => { onOpenChange(o); if (!o) { setStep("input"); setPlan(null); setClar(null); } };
    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent data-testid="ai-prompt-dialog" className="w-[calc(100vw-1rem)] max-w-2xl grid-cols-[minmax(0,1fr)] bg-[#111827] border-[#1e293b] text-slate-100 max-h-[90vh] overflow-auto overflow-x-hidden">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-mono"><Sparkles className="w-4 h-4 text-fuchsia-400" /> {t("pr_title")}</DialogTitle>
                    <DialogDescription className="text-slate-400">{t("pr_desc")}</DialogDescription>
                </DialogHeader>
                {step === "input" && (
                    <div data-testid="pr-input-step">
                        <textarea data-testid="pr-prompt" autoFocus value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={400} rows={2} placeholder={t("pr_ex_1")}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
                            className="w-full px-3 py-2 rounded-md bg-[#0b0f17] border border-[#1e293b] text-sm resize-none focus:outline-none focus:ring-1 focus:ring-fuchsia-500" />
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            {EXAMPLES.map((e) => <button key={e} data-testid={`pr-example-${e}`} onClick={() => setPrompt(t(e))} className={`${chip} border-[#1e293b] text-slate-300 hover:bg-[#182232]`}>{t(e)}</button>)}
                            <button data-testid="pr-ask" disabled={busy || prompt.trim().length < 2} onClick={() => ask()}
                                className="ml-auto h-8 px-4 rounded text-xs font-semibold bg-fuchsia-600 hover:bg-fuchsia-500 text-white disabled:opacity-40 flex items-center gap-1.5">
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {t("pr_ask")}</button>
                        </div>
                        <Library t={t} templates={templates} onPick={(p) => { setPrompt(p.prompt); toPlan(p); }} />
                    </div>
                )}
                {step === "clarify" && clar && <Clarify t={t} clar={clar.clarify} onBack={() => setStep("input")} onDone={(fields) => toPlan({ ...clar, prompt: prompt.trim(), fields: [{ key: "name", label: "name" }, ...fields].filter((f, i, a) => a.findIndex((x) => (x.key || x.label) === (f.key || f.label)) === i) })} />}
                {step === "plan" && plan && <Plan t={t} plan={plan} setPlan={setPlan} templates={templates} onEdit={() => setStep("input")} onRun={(r) => { close(false); onRun(r); }} />}
            </DialogContent>
        </Dialog>
    );
}
